import React, { useEffect, useState } from 'react';
import { BoxSkeleton, CardHeader, CardSection } from '@europrocurement/flexy-components';
import { DOC_TYPE_DOWNLOADABLE } from '@europrocurement/l2d-domain';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers';
import { useDossierDocumentSignContext, useCurrentDossierCtx } from '../../../providers';
import DownloadableDocumentsList from '../DownloadableDocumentsList/DownloadableDocumentsList';
import { type DisplayedDocument } from '../../../providers/types';
import useDossierFile from '../../../hooks/useDossierFile';
import useDocumentSign from '../../../hooks/useDocumentSign';
import useDocumentSignRender from '../../../hooks/useDocumentSignRender';
import { CATEGORY_LEGAL_ANNOUCEMENT } from '../../../constants/articlesCategories';
import { VALIDATOR_TYPE_LEGAL_ANNOUCEMENT } from '../../../constants/validators';
import useDossierArticle, { type DossierArticle } from '../../../hooks/useDossierArticle';
import useDossierProcessesActionsAndRender from '../../../hooks/useDossierProcessesActionsAndRender';
import RequiredDocumentsList from '../RequiredDocumentsList/RequiredDocumentsList';
import { LegalAnnoucementJustif } from '../../../hooks/useDossierLegalAnnoucements';
import NoResult from '../NoResult/NoResult';

export interface DownloadableDocumentsTabPros {
    source?: 'BACK' | 'FRONT';
}

const DownloadableDocumentsTab: React.FunctionComponent<DownloadableDocumentsTabPros> = function ({
    source = 'BACK',
}: DownloadableDocumentsTabPros) {
    const [listItems, setListItems] = useState<Array<DisplayedDocument>>([]);
    const [externalListItems, setExternalListItems] = useState<Array<DisplayedDocument>>([]);
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucementJustif>>([]);
    const [prestaArticles, setPrestaArticles] = useState<DossierArticle[]>();
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>();

    const { loadLegalAnnoucementsJustifs } = useDossierProcessesActionsAndRender();

    const { dossier } = useCurrentDossierCtx();
    const { handleSignSingleDocument } = useDocumentSign();
    const { showSignedDocument } = useDocumentSignRender();
    const { getDossiersBySignedFileId, getSignDocItems } = useDossierDocumentSignContext();
    const { dosDocs, getDossierDocumentsByCategory } = useDossierDocumentsCtx();
    const { getArticles } = useDossierArticle();

    const { handleDownload, handleShow, handleSplitDosDoc, submittingItemId } = useDossierFile({
        typeDoc: DOC_TYPE_DOWNLOADABLE,
        source,
    });

    const isSubmitting = submittingItemId !== null;

    const handleShowExternalUrl = (url: string) => {
        window.open(url, '_blank');
    };

    useEffect(() => {
        if (!dossier) {
            return;
        }

        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);
            loadLegalAnnoucementsJustifs(dossier, CATEGORY_LEGAL_ANNOUCEMENT, [
                VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
            ])
                .then((items: LegalAnnoucementJustif[]) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    setLegalAnnoucements([]);
                });
        }

        getArticles(dossier.id as number, CATEGORY_LEGAL_ANNOUCEMENT).then(
            (res: DossierArticle[]) => setPrestaArticles(res),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    const handleSignSingleDocProxy = (label: string, fichierId: number, fileName?: string) => {
        if (!dossier) {
            return;
        }
        handleSignSingleDocument(
            dossier.id as number,
            dossier.prescripteurId as number,
            dossier.prescripteurLibelle as string,
            dossier.prescripteurEmail as string,
            label,
            fichierId,
            fileName,
        );
    };

    const handleDownloadSigned = (fichierId: number, fileName: string) => {
        showSignedDocument(fichierId, fileName, getDossiersBySignedFileId(fichierId));
    };

    useEffect(() => {
        if (!dossier) {
            return;
        }
        if (prestaArticles?.length === 0) {
            return;
        }
        const allDownloadableDocs = [
            ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_DOWNLOADABLE }),
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_DOWNLOADABLE,
                additionalDocs: true,
            }),
        ];

        const downloadableFichierIds = new Set(
            allDownloadableDocs.map((d) => d.fichierId).filter((id): id is number => id != null),
        );

        const downloadableDocs = allDownloadableDocs
            .filter((doc) => doc.status === 'success')
            .map((doc) => ({
                ...doc,
                canDownloadSigned: false,
                movable: false,
                editable: false,
                removable: false,
                removableDoc: false,
                signable: false,
            }));

        const shownFichierIds = new Set(
            downloadableDocs.map((d) => d.fichierId).filter((id): id is number => id != null),
        );
        const signDocItems = getSignDocItems({
            referenceFichierIds: downloadableFichierIds,
            excludeFichierIds: shownFichierIds,
            onlySigned: true,
        });

        // Only display validated documents and remove actions
        setListItems([...downloadableDocs, ...signDocItems]);

        let externalDocLegal: DisplayedDocument[] = [];

        legalAnnoucements.map((legal) => {
            const validatorProcess = dossier.validationProcesses.find(
                (process) => process.identifier === legal.id.toString(),
            );
            if (validatorProcess) {
                const prestaArticle = prestaArticles?.find((article) =>
                    validatorProcess.articles.includes(article.id),
                );

                if (prestaArticle) {
                    externalDocLegal = [
                        ...externalDocLegal,
                        {
                            dossierId: dossier.id as number,
                            downloadable: false,
                            fileName: legal.justif,
                            label: `${validatorProcess.validatorLabel} ${legal.numero}` as string,
                            uploadable: false,
                            id: legal.id,
                            uploading: false,
                            editable: false,
                            showable: true,
                            signable: true,
                            canDownloadSigned: false,
                            removable: false,
                            removableDoc: false,
                            hasType: false,
                            movable: false,
                            showStatus: false,
                        },
                    ];
                }
            }
            return true;
        });

        setExternalListItems(externalDocLegal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dosDocs, legalAnnoucements, prestaArticles, getDossiersBySignedFileId, getSignDocItems]);

    if (!dossier) {
        return (
            <BoxSkeleton
                sx={{
                    height: '250px',
                }}
            />
        );
    }

    return (
        <CardSection
            header={
                <CardHeader
                    title="Documents remis"
                    titleSx={{
                        fontSize: '20px',
                        lineHeight: '28px',
                    }}
                    border={false}
                />
            }
            sxContent={{
                padding: '8px 32px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px 0',
            }}
        >
            {externalListItems.length > 0 && (
                <DownloadableDocumentsList
                    documents={externalListItems}
                    onDownload={handleDownload}
                    onShow={handleShowExternalUrl}
                />
            )}

            <RequiredDocumentsList
                documents={listItems}
                onUpload={() => {}}
                onDeletion={() => {}}
                onDocDeletion={() => {}}
                onDownload={handleDownload}
                onShow={handleShow}
                onSign={handleSignSingleDocProxy}
                onDownloadSigned={handleDownloadSigned}
                onMove={() => {}}
                onSplitDosDoc={handleSplitDosDoc}
                isSubmitting={isSubmitting}
                submittingItemId={submittingItemId}
            />

            {listItems.length === 0 && externalListItems.length === 0 && (
                <NoResult
                    title="En attente de documents remis"
                    description="Les fichiers partagés seront affichés ici dès leur réception"
                    paddingSx="100px 0 150px 0"
                />
            )}
        </CardSection>
    );
};

export default DownloadableDocumentsTab;
